import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { parseAuthFromUrl } from '../lib/parseAuthFromUrl';
import { log } from '../lib/logger';

export default function Onboarding() {
  log.info('Onboarding boot');
  const navigate = useNavigate();
  const [mode, setMode] = useState<'exchanging' | 'collect' | 'submitting' | 'error'>('exchanging');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        const href = window.location.href;
        const { fromHash, fromQuery } = parseAuthFromUrl(href);

        log.info('[Onboarding] initial hash:', window.location.hash);
        log.info('[Onboarding] initial search:', window.location.search);

        if (fromHash.access_token && fromHash.refresh_token) {
          log.info('[Onboarding] Using implicit flow (setSession)');
          const { data, error } = await supabase.auth.setSession({
            access_token: fromHash.access_token,
            refresh_token: fromHash.refresh_token,
          });

          if (cancelled) return;

          if (error) {
            log.warn('[Onboarding] setSession failed:', error.message);
            setMode('error');
            setErrorMsg(error.message || 'Invalid or expired invite link');
            return;
          }

          if (data?.session) {
            log.info('[Onboarding] session established (implicit), showing profile form');
            try {
              history.replaceState(null, '', '/onboarding');
            } catch {}
            setMode('collect');
            return;
          }
        }

        if (fromQuery.code) {
          log.info('[Onboarding] Using PKCE flow (exchangeCodeForSession)');
          const { data, error } = await supabase.auth.exchangeCodeForSession(href);

          if (cancelled) return;

          if (error) {
            log.warn('[Onboarding] exchangeCodeForSession failed:', error.message);
            setMode('error');
            setErrorMsg(error.message || 'Invalid or expired invite link');
            return;
          }

          if (data?.session) {
            log.info('[Onboarding] session established (pkce), showing profile form');
            try {
              history.replaceState(null, '', '/onboarding');
            } catch {}
            setMode('collect');
            return;
          }
        }

        log.info('[Onboarding] No auth tokens found');
        setMode('error');
        setErrorMsg('No invite token found. Please use the invite link from your email.');
      } catch (error: any) {
        log.error('[Onboarding] Unexpected error:', error);
        setMode('error');
        setErrorMsg(error.message || 'An unexpected error occurred');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setMode('submitting');

    try {
      if (!fullName.trim()) {
        throw new Error('Please enter your name');
      }
      if (password.length < 8) {
        throw new Error('Password must be at least 8 characters');
      }

      log.debug('[Onboarding] Updating user profile...');
      const { error } = await supabase.auth.updateUser({
        data: { full_name: fullName.trim() },
        password,
      });

      if (error) throw error;

      log.info('[Onboarding] profile saved, redirecting to /welcome');
      navigate('/welcome', { replace: true });
    } catch (err: any) {
      log.error('[Onboarding] Profile update error:', err);
      setErrorMsg(err?.message || 'Could not save your details, please try again.');
      setMode('collect');
    }
  };

  if (mode === 'exchanging') {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div>⏳ Completing sign-in…</div>
      </div>
    );
  }

  if (mode === 'error') {
    return (
      <div
        style={{
          maxWidth: 420,
          margin: '48px auto',
          padding: 24,
          border: '1px solid #eee',
          borderRadius: 12,
        }}
      >
        <h1>Authentication Error</h1>
        <p style={{ color: 'crimson' }}>{errorMsg}</p>
        <button onClick={() => window.location.reload()} style={{ padding: '10px 16px' }}>
          Try again
        </button>
      </div>
    );
  }

  if (mode === 'collect' || mode === 'submitting') {
    return (
      <div
        style={{
          maxWidth: 480,
          margin: '48px auto',
          padding: 24,
          border: '1px solid #eee',
          borderRadius: 12,
        }}
      >
        <h1>Complete your account</h1>
        <p>Set your name and password to finish your registration.</p>
        {errorMsg && <p style={{ color: 'crimson', marginTop: 12 }}>{errorMsg}</p>}
        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', marginTop: 16 }}>
            Full name
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              disabled={mode === 'submitting'}
              style={{
                display: 'block',
                width: '100%',
                padding: 10,
                marginTop: 6,
                opacity: mode === 'submitting' ? 0.6 : 1,
              }}
              placeholder="Enter your full name"
            />
          </label>
          <label style={{ display: 'block', marginTop: 16 }}>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              disabled={mode === 'submitting'}
              style={{
                display: 'block',
                width: '100%',
                padding: 10,
                marginTop: 6,
                opacity: mode === 'submitting' ? 0.6 : 1,
              }}
              placeholder="Create a strong password (min 8 characters)"
            />
          </label>
          <button
            type="submit"
            disabled={mode === 'submitting'}
            style={{
              marginTop: 20,
              padding: '10px 20px',
              opacity: mode === 'submitting' ? 0.6 : 1,
              cursor: mode === 'submitting' ? 'not-allowed' : 'pointer',
            }}
          >
            {mode === 'submitting' ? 'Saving...' : 'Save and continue'}
          </button>
        </form>
      </div>
    );
  }

  return null;
}
